/**
 * Setările instanței — vizibile doar adminului global (rol acordat din linia
 * de comandă). Serverul verifică rolul la fiecare cerere; aici doar ascundem
 * secțiunea pentru ceilalți.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { authApi } from '@/api/auth';
import { errorMessage } from '@/api/client';
import { systemApi, type CleanupResult, type SystemSetting } from '@/api/system';
import { formatDateTime } from '@/lib/format';

export default function SystemSettingsSection() {
  const { t } = useTranslation();
  const me = useQuery({ queryKey: ['me'], queryFn: authApi.me, staleTime: 60_000 });
  const isAdmin = me.data?.is_global_admin === true;
  const settings = useQuery({
    queryKey: ['system-settings'],
    queryFn: systemApi.list,
    enabled: isAdmin,
  });

  if (!isAdmin) return null;

  return (
    <div className="card" data-testid="system-settings">
      <div className="mb-4">
        <h2 className="display-title-sm">{t('system_settings.title')}</h2>
        <p className="text-xs text-[var(--text-muted)] mt-0.5">
          {t('system_settings.subtitle')}
        </p>
      </div>

      {settings.isLoading && (
        <p className="text-sm text-[var(--text-muted)]">{t('common.loading')}</p>
      )}
      {settings.isError && (
        <p className="text-sm" style={{ color: 'var(--danger)' }}>
          {errorMessage(settings.error)}
        </p>
      )}

      <div className="space-y-4">
        {settings.data?.map((s) => <SettingRow key={s.key} setting={s} />)}
        {settings.data && <CleanupNow />}
      </div>
    </div>
  );
}

function SettingRow({ setting }: { setting: SystemSetting }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [value, setValue] = useState(setting.value);
  const [saved, setSaved] = useState(false);
  const inputId = `system-setting-${setting.key}`;
  const valid = value >= setting.minimum && value <= setting.maximum;

  const save = useMutation({
    mutationFn: () => systemApi.update(setting.key, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    },
  });

  return (
    <div>
      <label
        htmlFor={inputId}
        className="block text-xs font-medium text-[var(--text-muted)] mb-1"
      >
        {t(`system_settings.keys.${setting.key}.label`)}
      </label>
      <div className="flex flex-wrap gap-2 items-center">
        <input
          id={inputId}
          type="number"
          min={setting.minimum}
          max={setting.maximum}
          className="input w-32"
          value={value}
          onChange={(e) => setValue(Number(e.target.value) || 0)}
        />
        <span className="text-xs text-[var(--text-muted)]">
          {t('system_settings.range', {
            min: setting.minimum,
            max: setting.maximum,
            default: setting.default,
          })}
        </span>
        <button
          type="button"
          className="btn-primary text-xs ml-2"
          onClick={() => save.mutate()}
          disabled={!valid || value === setting.value || save.isPending}
        >
          {t('common.save')}
        </button>
        {saved && (
          <span className="text-xs text-green-600 dark:text-green-400">
            {t('admin_ui.settings.saved')}
          </span>
        )}
      </div>
      <p className="text-xs text-[var(--text-muted)] mt-1">
        {t(`system_settings.keys.${setting.key}.hint`)}
        {setting.updated_at &&
          ` ${t('system_settings.updated_at', { when: formatDateTime(setting.updated_at) })}`}
      </p>
      {save.isError && (
        <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>
          {errorMessage(save.error)}
        </p>
      )}
    </div>
  );
}

function CleanupNow() {
  const { t } = useTranslation();
  const [result, setResult] = useState<CleanupResult | null>(null);
  const run = useMutation({
    mutationFn: systemApi.runCleanup,
    onSuccess: setResult,
  });

  return (
    <div>
      <div className="flex flex-wrap gap-3 items-center">
        <button
          type="button"
          className="btn-ghost text-xs"
          onClick={() => run.mutate()}
          disabled={run.isPending}
        >
          {run.isPending ? t('system_settings.cleanup_running') : t('system_settings.cleanup_now')}
        </button>
        {result && (
          <span className="text-xs text-[var(--text-muted)]" data-testid="cleanup-result">
            {t('system_settings.cleanup_result', {
              sessions: result.sessions,
              invites: result.invites,
            })}
          </span>
        )}
      </div>
      <p className="text-xs text-[var(--text-muted)] mt-1">
        {t('system_settings.cleanup_hint')}
      </p>
      {run.isError && (
        <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>
          {errorMessage(run.error)}
        </p>
      )}
    </div>
  );
}
